import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { Bot, ChevronDown, Ellipsis, ImageOff, Paperclip, PenLine, Send, Type } from 'lucide-react';
import type { MessageView } from '@mailpilot/shared';
import { api } from '../../lib/api';
import { displayName, formatAddressList, fullDate, relativeTime } from '../../lib/format';
import { Avatar } from '../ui/Avatar';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { HtmlFrame } from '../ui/HtmlFrame';
import { useToast } from '../ui/Toast';
import { AttachmentChip } from './AttachmentChip';
import { spring } from '../ui/motion';

/** Small pill-shaped toggle used in the message footer (quoted text, signature, plain text). */
function FooterToggle({ icon, children, onClick, pressed, label }: { icon: React.ReactNode; children: React.ReactNode; onClick: () => void; pressed?: boolean; label?: string }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={pressed} aria-label={label} className="mp-chip !h-7 !px-2.5 !text-[0.6875rem]">
      {icon}
      {children}
    </button>
  );
}

export function MessageItem({
  m,
  defaultOpen,
  highlighted,
  blockRemoteImages,
  isOnlyMessage,
}: {
  m: MessageView;
  defaultOpen: boolean;
  highlighted: boolean;
  blockRemoteImages: boolean;
  isOnlyMessage: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [details, setDetails] = useState(false);
  const [showQuoted, setShowQuoted] = useState(false);
  const [showSig, setShowSig] = useState(false);
  const [html, setHtml] = useState<string | null>(m.bodyHtml);
  const [loadingImages, setLoadingImages] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [view, setView] = useState<'html' | 'text'>(m.bodyHtml ? 'html' : 'text');
  const toast = useToast();

  useEffect(() => setHtml(m.bodyHtml), [m.bodyHtml]);
  useEffect(() => {
    if (highlighted) setOpen(true);
  }, [highlighted]);

  const loadImages = async () => {
    setLoadingImages(true);
    try {
      const r = await api('GET /api/messages/:id/remote-content', { params: { id: m.id } });
      setHtml(r.bodyHtml);
      setImagesLoaded(true);
      setView('html');
    } catch (e) {
      toast.error(e);
    } finally {
      setLoadingImages(false);
    }
  };
  useEffect(() => {
    if (open && !blockRemoteImages && m.hasRemoteImages && !imagesLoaded && m.bodyHtml) void loadImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, blockRemoteImages, m.id]);

  const sender = displayName(m.from);
  const toLine = formatAddressList(m.to) || '—';
  const ccLine = m.cc.length > 0 ? formatAddressList(m.cc) : '';
  const detailsId = `msg-${m.id}-details`;

  return (
    <article
      id={`msg-${m.id}`}
      aria-label={`Message from ${sender}, ${fullDate(m.date)}`}
      className={clsx(
        'scroll-mt-24 overflow-hidden rounded-card border border-hairline bg-surface shadow-card transition-shadow',
        highlighted && 'evidence-highlight ring-2 ring-warn/60',
        !open && 'hover:shadow-panel',
      )}
    >
      {/* ---- header / collapsed row ---- */}
      <div className={clsx('flex items-start gap-3', open ? 'px-5 pt-5 pb-3' : 'px-5 py-3.5')}>
        <button
          type="button"
          onClick={() => !isOnlyMessage && setOpen((o) => !o)}
          aria-expanded={open}
          disabled={isOnlyMessage}
          className={clsx('flex min-w-0 flex-1 items-start gap-3 rounded-xl text-left', !isOnlyMessage && 'cursor-pointer')}
        >
          <Avatar name={m.from.name} email={m.from.email} size={open ? 40 : 32} className={clsx('transition-all', !open && 'mt-0.5')} />
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className={clsx('truncate text-[0.9375rem] font-semibold leading-snug text-ink', m.unread && 'text-primary-ink')}>{sender}</span>
              {open && <span className="truncate text-[0.8125rem] text-muted">{m.from.email}</span>}
              {m.isOutgoing && (
                <Badge tone="primary" icon={<Send className="h-3 w-3" aria-hidden />}>
                  Sent
                </Badge>
              )}
              {m.isAutomated && (
                <Badge tone="neutral" icon={<Bot className="h-3 w-3" aria-hidden />} title={m.automatedReasons.join('; ')}>
                  Automated
                </Badge>
              )}
            </span>
            {open ? (
              <span className="mt-0.5 block truncate text-[0.8125rem] text-muted">
                to {toLine}
                {ccLine && <> · cc {ccLine}</>}
              </span>
            ) : (
              <span className="mt-0.5 line-clamp-1 text-[0.8125rem] text-muted">{m.visibleText}</span>
            )}
          </span>
        </button>

        <div className="flex shrink-0 items-center gap-1 pt-0.5">
          <time dateTime={m.date} title={fullDate(m.date)} className="text-xs tabular-nums text-muted">
            {open ? fullDate(m.date) : relativeTime(m.date)}
          </time>
          {open && (
            <button
              type="button"
              aria-expanded={details}
              aria-controls={detailsId}
              onClick={() => setDetails((d) => !d)}
              className="tip tip-bottom-end ml-1 inline-flex h-7 w-7 items-center justify-center rounded-lg text-muted transition-colors hover:bg-raised hover:text-ink"
              data-tip={details ? 'Hide details' : 'Show details'}
              aria-label={details ? 'Hide message details' : 'Show message details'}
            >
              <ChevronDown className={clsx('h-4 w-4 transition-transform', details && 'rotate-180')} aria-hidden />
            </button>
          )}
          {!isOnlyMessage && (
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              aria-label={open ? 'Collapse message' : 'Expand message'}
              className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-muted transition-colors hover:bg-raised hover:text-ink"
            >
              <ChevronDown className={clsx('h-4 w-4 transition-transform', open && 'rotate-180')} aria-hidden />
            </button>
          )}
        </div>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={spring} className="overflow-hidden">
            <div className="px-5 pb-5">
              {/* ---- expandable envelope details ---- */}
              <AnimatePresence initial={false}>
                {details && (
                  <motion.dl
                    id={detailsId}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={spring}
                    className="mb-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 overflow-hidden rounded-xl bg-raised px-4 py-3 text-[0.8125rem]"
                  >
                    <dt className="text-muted">From</dt>
                    <dd className="min-w-0 break-words text-ink">
                      {m.from.name ? `${m.from.name} <${m.from.email}>` : m.from.email}
                    </dd>
                    <dt className="text-muted">To</dt>
                    <dd className="min-w-0 break-words text-ink">{toLine}</dd>
                    {ccLine && (
                      <>
                        <dt className="text-muted">Cc</dt>
                        <dd className="min-w-0 break-words text-ink">{ccLine}</dd>
                      </>
                    )}
                    <dt className="text-muted">Date</dt>
                    <dd className="text-ink">{fullDate(m.date)}</dd>
                  </motion.dl>
                )}
              </AnimatePresence>

              {m.isAutomated && m.automatedReasons.length > 0 && (
                <p className="mb-3 flex items-start gap-2 rounded-xl bg-raised px-3 py-2 text-xs text-muted">
                  <Bot className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span>
                    Detected as an automatic message ({m.automatedReasons.join(', ')}). MailPilot never auto-replies to these.
                  </span>
                </p>
              )}

              {m.hasRemoteImages && !imagesLoaded && view === 'html' && (
                <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-hairline bg-raised/70 px-3.5 py-2.5 text-xs text-muted">
                  <ImageOff className="h-4 w-4 shrink-0" aria-hidden />
                  <span className="min-w-0 flex-1">Remote images are blocked to protect your privacy — they can be used for tracking.</span>
                  <Button size="sm" variant="soft" loading={loadingImages} onClick={loadImages} className="!h-7 !rounded-lg !px-2.5 !text-xs">
                    Load images
                  </Button>
                </div>
              )}

              {/* ---- body ---- */}
              {view === 'html' && html ? (
                <HtmlFrame html={html} title={`Email from ${sender}`} />
              ) : (
                <div className="reading max-w-[70ch] whitespace-pre-wrap break-words text-ink">{m.visibleText || m.bodyText}</div>
              )}

              {view === 'text' && showSig && m.signatureText && (
                <div className="mt-3 whitespace-pre-wrap border-l-2 border-line pl-3 text-[0.8125rem] leading-relaxed text-muted">{m.signatureText}</div>
              )}
              {m.quotedText && showQuoted && (
                <blockquote className="mt-3 whitespace-pre-wrap border-l-2 border-line pl-3 text-[0.8125rem] leading-relaxed text-muted">{m.quotedText}</blockquote>
              )}

              {/* ---- footer toggles ---- */}
              {(m.quotedText || (view === 'text' && m.signatureText) || m.bodyHtml) && (
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  {m.quotedText && (
                    <FooterToggle icon={<Ellipsis className="h-3.5 w-3.5" aria-hidden />} onClick={() => setShowQuoted((s) => !s)} pressed={showQuoted} label={showQuoted ? 'Hide quoted text' : 'Show quoted text'}>
                      {showQuoted ? 'Hide quoted text' : 'Quoted text'}
                    </FooterToggle>
                  )}
                  {view === 'text' && m.signatureText && (
                    <FooterToggle icon={<PenLine className="h-3.5 w-3.5" aria-hidden />} onClick={() => setShowSig((s) => !s)} pressed={showSig}>
                      {showSig ? 'Hide signature' : 'Signature'}
                    </FooterToggle>
                  )}
                  {m.bodyHtml && (
                    <FooterToggle icon={<Type className="h-3.5 w-3.5" aria-hidden />} onClick={() => setView(view === 'html' ? 'text' : 'html')} pressed={view === 'text'} label={view === 'html' ? 'Show plain text' : 'Show formatted message'}>
                      {view === 'html' ? 'Plain text' : 'Formatted'}
                    </FooterToggle>
                  )}
                </div>
              )}

              {/* ---- attachments ---- */}
              {m.attachments.length > 0 && (
                <div className="mt-4 border-t border-line/60 pt-4">
                  <p className="mb-2 flex items-center gap-1.5 text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle">
                    <Paperclip className="h-3.5 w-3.5" aria-hidden />
                    {m.attachments.length} attachment{m.attachments.length === 1 ? '' : 's'}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {m.attachments.map((a) => (
                      <AttachmentChip key={a.id} a={a} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </article>
  );
}
